import {
    Component,
    OnInit,
    OnChanges,
    SimpleChanges,
    Input,
    Output,
    EventEmitter,
    inject,
    signal,
    OnDestroy,
    computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormsModule, FormGroup, Validators, FormBuilder } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ClientRecordsService } from '../../../core/services/client-records.service';
import { EntityCacheService } from '../../../core/services/entity-cache.service';
import { ContactResponseDto } from '@qc/shared';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject, BehaviorSubject } from 'rxjs';

@Component({
    selector: 'qc-contact-selector',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormsModule,
        InputTextModule,
        DropdownModule,
        ButtonModule,
        DialogModule,
    ],
    template: `
        <div class="form-field">
            <label class="block text-sm mb-1">
                {{ label }}
                <span *ngIf="required">*</span>
            </label>
            <div *ngIf="!selectedContact()" class="space-y-3">
                <p-dropdown
                    [options]="filteredContacts"
                    [(ngModel)]="selectedContactForModel"
                    optionLabel="fullName"
                    placeholder="Select a contact..."
                    (onChange)="onContactSelected($event)"
                    (onShow)="onDropdownShow()"
                    [showClear]="true"
                    [filter]="true"
                    filterBy="fullName"
                    (onFilter)="onSearchChange($event.filter)"
                    styleClass="w-full"
                    panelStyleClass="dropdown-panel-overflow-visible"
                    scrollHeight="200px"
                    [class.border-rose-500]="hasValidationError()"
                    [disabled]="disabled"
                    [loading]="isSearching"
                >
                    <ng-template let-contact pTemplate="item">
                        <div class="p-3">
                            <div class="font-medium text-gray-900">{{ contact.fullName }}</div>
                            <div class="text-sm text-gray-500 mt-1" *ngIf="contact.positionTitle">
                                {{ contact.positionTitle }}
                            </div>
                            <div class="text-xs text-gray-400 mt-1" *ngIf="contact.email">{{ contact.email }}</div>
                        </div>
                    </ng-template>
                </p-dropdown>
                <div *ngIf="showCreateButton" class="text-center mt-3">
                    <p-button
                        severity="secondary"
                        variant="outlined"
                        (click)="openCreateModal()"
                        icon="pi pi-plus"
                        label="Add New Contact"
                        size="small"
                        [disabled]="disabled"
                    />
                </div>
            </div>
            <div
                *ngIf="selectedContact()"
                class="flex justify-between items-start border border-gray-200 rounded-md p-3"
            >
                <div>
                    <div class="font-medium text-gray-900">{{ selectedContact()?.fullName }}</div>
                    <div class="text-sm text-gray-500 mt-1" *ngIf="selectedContact()?.positionTitle">
                        {{ selectedContact()?.positionTitle }}
                    </div>
                    <div class="text-xs text-gray-400 mt-1" *ngIf="selectedContact()?.email">
                        {{ selectedContact()?.email }}
                    </div>
                </div>
                <p-button
                    severity="secondary"
                    variant="text"
                    (click)="clearSelection()"
                    icon="pi pi-times"
                    size="small"
                    [disabled]="disabled"
                />
            </div>
            <div *ngIf="hasValidationError()" class="text-rose-500 text-sm mt-1">
                {{ getValidationError() }}
            </div>
        </div>

        <p-dialog
            [(visible)]="showCreateModal"
            [modal]="true"
            [style]="{ width: '50vw' }"
            [draggable]="false"
            [resizable]="false"
            header="Create New Contact"
            styleClass="p-dialog-header-minimize"
        >
            <form [formGroup]="createForm" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block mb-2"> Full Name * </label>
                        <input
                            pInputText
                            formControlName="fullName"
                            placeholder="Enter full name"
                            class="w-full"
                            [class.border-rose-500]="hasCreateFieldError('fullName')"
                        />
                        <div *ngIf="getCreateFieldError('fullName')" class="text-rose-500 text-sm mt-1">
                            {{ getCreateFieldError('fullName') }}
                        </div>
                    </div>

                    <div>
                        <label class="block mb-2"> Position Title </label>
                        <input
                            pInputText
                            formControlName="positionTitle"
                            placeholder="Job title or role"
                            class="w-full"
                        />
                    </div>

                    <div>
                        <label class="block mb-2"> Email * </label>
                        <input
                            pInputText
                            formControlName="email"
                            type="email"
                            placeholder="contact@example.com"
                            class="w-full"
                            [class.border-rose-500]="hasCreateFieldError('email')"
                        />
                        <div *ngIf="getCreateFieldError('email')" class="text-rose-500 text-sm mt-1">
                            {{ getCreateFieldError('email') }}
                        </div>
                    </div>

                    <div>
                        <label class="block mb-2"> Phone </label>
                        <input pInputText formControlName="phone" placeholder="+1 (555) 123-4567" class="w-full" />
                    </div>
                </div>
            </form>

            <div class="flex justify-end gap-2 mt-6">
                <p-button
                    severity="secondary"
                    variant="outlined"
                    (click)="closeCreateModal()"
                    label="Cancel"
                    size="small"
                />
                <p-button
                    severity="primary"
                    (click)="saveNewContact()"
                    label="Create Contact"
                    size="small"
                    [disabled]="!createForm.valid"
                />
            </div>
        </p-dialog>
    `,
})
export class ContactSelectorComponent implements OnInit, OnDestroy, OnChanges {
    @Input() label: string = 'Contact';
    @Input() required: boolean = false;
    @Input() disabled: boolean = false;
    @Input() showCreateButton: boolean = true;
    @Input() initialValue: Partial<ContactResponseDto> | null = null;
    @Input() deferSave: boolean = false; 
    @Output() contactSelected = new EventEmitter<ContactResponseDto>();
    @Output() contactCreated = new EventEmitter<ContactResponseDto>(); 
    @Output() contactCleared = new EventEmitter<void>();
    @Output() validationChange = new EventEmitter<boolean>();

    private clientRecordsService = inject(ClientRecordsService);
    private entityCacheService = inject(EntityCacheService);
    private fb = inject(FormBuilder);
    private destroy$ = new Subject<void>();
    private searchTerms$ = new BehaviorSubject<string>('');

    selectedContact = signal<ContactResponseDto | null>(null);
    selectedContactForModel: ContactResponseDto | null = null;
    allContacts = signal<ContactResponseDto[]>([]);
    filteredContacts: ContactResponseDto[] = [];
    isSearching = false;

    createForm!: FormGroup;
    showCreateModal = false;
    touched = signal<boolean>(false);

    private loadedContactId: string | null = null;

    isValid = computed(() => {
        if (!this.required) return true;
        return !!this.selectedContact();
    });

    hasValidationError = computed(() => {
        return this.required && this.touched() && !this.selectedContact();
    });

    ngOnInit(): void {
        this.setupCreateForm();
        this.setupSearch();
        this.handleInitialValue();
        this.emitValidationChange();
        // Contacts will be loaded lazily when dropdown is opened
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialValue']) {
            this.handleInitialValue();
        }
        if (changes['required']) {
            this.emitValidationChange();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private setupSearch(): void {
        this.searchTerms$
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((searchTerm) => {
                    this.isSearching = true;
                    return this.entityCacheService.getContacts(searchTerm, 1, 100);
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: (result) => {
                    this.filteredContacts = result.items;
                    this.isSearching = false;
                },
                error: (error) => {
                    this.isSearching = false;
                },
            });
    }

    private loadInitialContacts(): void {
        this.entityCacheService
            .getContacts('', 1, 100)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result) => {
                    this.allContacts.set(result.items);
                    this.filteredContacts = result.items;
                },
                error: (error) => { },
            });
    }

    onSearchChange(searchTerm: string): void {
        this.searchTerms$.next(searchTerm);
    }

    onDropdownShow(): void {
        if (this.allContacts().length === 0) {
            this.loadInitialContacts();
        }
    }

    onContactSelected(event: any): void {
        this.touched.set(true);
        const contact = event.value;
        if (contact) {
            this.selectedContact.set(contact);
            this.selectedContactForModel = contact;
            this.contactSelected.emit(contact);
        } else {
            this.selectedContact.set(null);
            this.selectedContactForModel = null;
            this.contactCleared.emit();
        }
        this.emitValidationChange();
    }

    clearSelection(): void {
        this.selectedContact.set(null);
        this.selectedContactForModel = null;
        this.contactCleared.emit();
        this.emitValidationChange();
    }

    private setupCreateForm(): void {
        this.createForm = this.fb.group({
            fullName: ['', [Validators.required]],
            positionTitle: [''],
            email: ['', [Validators.required, Validators.email]],
            phone: [''],
        });
    }

    private handleInitialValue(): void {
        if (this.initialValue && typeof this.initialValue === 'object') {
            if (this.initialValue.fullName) {
                const contact = this.initialValue as ContactResponseDto;
                this.selectedContact.set(contact);
                this.selectedContactForModel = contact;
            } else if (this.initialValue.id && this.loadedContactId !== this.initialValue.id) {
                this.loadContactById(this.initialValue.id);
            }
        } else {
            this.selectedContact.set(null);
            this.selectedContactForModel = null;
            this.loadedContactId = null;
        }
        this.emitValidationChange();
    }

    private loadContactById(contactId: string): void {
        this.loadedContactId = contactId;
        this.clientRecordsService
            .getContact(contactId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (contact: ContactResponseDto) => {
                    this.selectedContact.set(contact);
                    this.selectedContactForModel = contact;
                    this.contactSelected.emit(contact);
                    this.emitValidationChange();
                },
                error: (error: any) => {
                    this.selectedContact.set(null);
                    this.selectedContactForModel = null;
                    this.emitValidationChange();
                },
            });
    }

    openCreateModal(): void {
        this.showCreateModal = true;
        this.touched.set(true);
    }

    closeCreateModal(): void {
        this.showCreateModal = false;
        this.createForm.reset();
    }

    saveNewContact(): void {
        if (this.createForm.valid) {
            const formValue = this.createForm.value;

            if (this.deferSave) {
                const contactData: ContactResponseDto = {
                    id: '', 
                    fullName: formValue.fullName || '',
                    positionTitle: formValue.positionTitle || '',
                    email: formValue.email || '',
                    phone: formValue.phone || '',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                };
                this.selectedContact.set(contactData);
                this.selectedContactForModel = contactData;
                this.contactCreated.emit(contactData);
                this.closeCreateModal();
                this.emitValidationChange();
                return;
            }

            this.clientRecordsService
                .createContact(formValue)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (contact: ContactResponseDto) => {
                        this.selectedContact.set(contact);
                        this.selectedContactForModel = contact;
                        this.contactSelected.emit(contact);
                        this.closeCreateModal();
                        this.allContacts.update((contacts) => [...contacts, contact]);
                        this.filteredContacts = [...this.filteredContacts, contact];
                        this.entityCacheService.invalidateContactsCache();
                        this.emitValidationChange();
                    },
                    error: (error: any) => { },
                });
        }
    }

    hasCreateFieldError(fieldName: string): boolean {
        const field = this.createForm.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getCreateFieldError(fieldName: string): string | null {
        const field = this.createForm.get(fieldName);
        if (!field || !field.errors || !field.touched) {
            return null;
        }

        if (field.errors['required']) {
            return `${fieldName} is required`;
        }
        if (field.errors['email']) {
            return 'Enter a valid email';
        }

        return 'Invalid field';
    }

    getValidationError(): string {
        if (!this.hasValidationError()) return '';
        return 'Please select a contact from the list or create a new one';
    }

    markAsTouched(): void {
        this.touched.set(true);
        this.emitValidationChange();
    }

    private emitValidationChange(): void {
        this.validationChange.emit(this.isValid());
    }
}
